<template>
  <div class="repayment-container">
    <!-- 顶部输入区 -->
    <div class="input-area">
      <div class="input-row">
        <div class="input-group">
          <div class="input-label">还款资金来源：</div>
          <select
            v-model="repaymentSource"
            class="input-select"
            :disabled="isViewMode"
            @change="handleRepaymentSourceChange"
          >
            <option value="netProfit">净利润</option>
            <option value="ebitda">息税折旧摊销前利润</option>
            <option value="profitBeforeTax">税前利润</option>
          </select>
          <div
            class="input-label"
            v-if="repaymentSource !== 'netProfit'"
            style="color: red"
          >
            （已扣除流动资金借款利息）
          </div>
        </div>
      </div>
    </div>

    <!-- 表格区域 -->
    <div class="table-wrapper">
      <div class="table-scroll-area">
        <table class="repayment-table">
          <thead>
            <tr>
              <th class="seq-col">序号</th>
              <th class="item-col">项 目</th>
              <th class="total-col">合计</th>
              <!-- 年份列，横向显示 -->
              <th
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="year"
              >
                {{ year }}
              </th>
            </tr>
            <tr>
              <th class="seq-col">-</th>
              <th class="item-col">建设期/运营期</th>
              <th class="total-col">-</th>
              <!-- 建设期列 -->
              <th
                class="year-col period-type-col construction-year"
                :colspan="projectData.constructionPeriod || 1"
              >
                建设期
              </th>
              <!-- 运营期列 -->
              <th
                class="year-col period-type-col operation-year"
                :colspan="
                  (projectData.calculationPeriod || 15) -
                  (projectData.constructionPeriod || 1)
                "
              >
                运营期
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- 贷款类型行 -->
            <template
              v-for="(loanType, loanIndex) in projectData.repaymentData || []"
            >
              <!-- 贷款类型主行 -->
              <tr
                class="category-row"
                :key="loanType.借款本金total + '-1category'"
              >
                <td class="seq-col">{{ loanIndex + 1 }}</td>
                <td class="item-col">{{ loanType.名称 }}</td>
                <td class="total-col">
                  {{ formatNumber(loanType.借款本金total) }}
                </td>
                <td
                  class="year-col"
                  v-for="year in projectData.calculationPeriod || 15"
                  :key="`1-${year}`"
                >
                  {{ formatNumber(loanType.借款本金[year - 1] || 0) }}
                </td>
              </tr>
              <!-- 期初本金行 -->
              <tr
                class="detail-row"
                :key="loanType.期初本金 + '-2openingPrincipal'"
              >
                <td class="seq-col"></td>
                <td class="item-col">期初本金</td>
                <td class="total-col"></td>
                <td
                  class="year-col"
                  v-for="year in projectData.calculationPeriod || 15"
                  :key="`2-openingPrincipal-${year}`"
                >
                  {{ formatNumber(loanType.期初本金[year - 1]) }}
                </td>
              </tr>
              <!-- 本年利息行 -->
              <tr
                class="detail-row"
                :key="loanType.当期还息total + '-3currentYearInterest'"
              >
                <td class="seq-col"></td>
                <td class="item-col">本年付息</td>
                <td class="total-col">
                  {{ formatNumber(loanType.当期还息total) }}
                </td>
                <td
                  class="year-col"
                  v-for="year in projectData.calculationPeriod || 15"
                  :key="`-30currentYearInterest-${year}`"
                >
                  {{ formatNumber(loanType.当期还息[year - 1]) }}
                </td>
              </tr>
              <!-- 本年利息行 -->
              <tr
                class="detail-row"
                :key="loanType.当期还本total + '-465currentYearInterest'"
              >
                <td class="seq-col"></td>
                <td class="item-col">本年还本</td>
                <td class="total-col">
                  {{ formatNumber(loanType.当期还本total) }}
                </td>
                <td
                  class="year-col"
                  v-for="year in projectData.calculationPeriod || 15"
                  :key="`-11currentYearInterest-${year}`"
                >
                  {{ formatNumber(loanType.当期还本[year - 1]) }}
                </td>
              </tr>
              <!-- 期末本金行 -->
              <tr
                class="detail-row"
                :key="loanType.期末本金total + '-90closingPrincipal'"
              >
                <td class="seq-col"></td>
                <td class="item-col">期末本金</td>
                <td class="total-col">
                  {{ formatNumber(loanType.期末本金total) }}
                </td>
                <td
                  class="year-col"
                  v-for="year in projectData.calculationPeriod || 15"
                  :key="`-5closingPrincipal-${year}`"
                >
                  {{ formatNumber(loanType.期末本金[year - 1]) }}
                </td>
              </tr>
            </template>

            <!-- 还款组行 -->
            <tr class="category-row">
              <td class="seq-col">
                {{ projectData.repaymentData.length + 1 }}
              </td>
              <td class="item-col">借款偿还</td>
              <td class="total-col"></td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`6repayment-group8-${year}`"
              ></td>
            </tr>
            <!-- 期初可用还款资金行 -->
            <tr class="detail-row">
              <td class="seq-col"></td>
              <td class="item-col">期初可用还款资金</td>
              <td class="total-col"></td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 1"
                :key="`repayment-7openingFunds-${year}`"
              >
                {{
                  formatNumber(
                    projectData.addedFunds.期初可用还款资金[year - 1],
                  )
                }}
              </td>
            </tr>
            <!-- 本年新增还款资金行 -->
            <tr class="detail-row">
              <td class="seq-col"></td>
              <td class="item-col">本年新增还款资金</td>
              <td class="total-col">
                {{ formatNumber(projectData.addedFunds.本年新增还款资金total) }}
              </td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`repayment-8addedFunds-${year}`"
              >
                {{
                  formatNumber(
                    projectData.addedFunds.本年新增还款资金[year - 1],
                  )
                }}
              </td>
            </tr>
            <!-- 当期本金还款行 -->
            <tr class="detail-row">
              <td class="seq-col"></td>
              <td class="item-col">当期本金还款</td>
              <td class="total-col">
                {{ formatNumber(projectData.addedFunds.当期本金还款total) }}
              </td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`repayment-0principalRepayment-${year}`"
              >
                {{
                  formatNumber(projectData.addedFunds.当期本金还款[year - 1])
                }}
              </td>
            </tr>
            <!-- 当期利息还款行 -->
            <tr class="detail-row">
              <td class="seq-col"></td>
              <td class="item-col">当期利息还款</td>
              <td class="total-col">
                {{ formatNumber(projectData.addedFunds.当期利息还款total) }}
              </td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`repayment-9interestRepayment-${year}`"
              >
                {{
                  formatNumber(projectData.addedFunds.当期利息还款[year - 1])
                }}
              </td>
            </tr>
            <!-- 期末可用资金行 -->
            <tr class="detail-row">
              <td class="seq-col"></td>
              <td class="item-col">期末可用资金</td>
              <td class="total-col">
                {{ formatNumber(projectData.addedFunds.期末可用资金合计total) }}
              </td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`repayment-10closingFunds-${year}`"
              >
                {{
                  formatNumber(projectData.addedFunds.期末可用资金[year - 1])
                }}
              </td>
            </tr>
            <!-- 当期本金还款行 -->
            <tr class="category-row">
              <td class="seq-col"></td>
              <td class="item-col">利息备付率</td>
              <td class="total-col">
                {{ formatNumber(projectData.addedFunds.利息备付率total) }}
              </td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`repayment-0princi9palRepayment-${year}`"
              >
                {{ formatNumber(projectData.addedFunds.利息备付率[year - 1]) }}
              </td>
            </tr>
            <!-- 当期利息还款行 -->
            <tr class="category-row">
              <td class="seq-col"></td>
              <td class="item-col">本金备付率</td>
              <td class="total-col">
                {{ formatNumber(projectData.addedFunds.本金备付率total) }}
              </td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`repayment-9inter9estRepayment-${year}`"
              >
                {{ formatNumber(projectData.addedFunds.本金备付率[year - 1]) }}
              </td>
            </tr>
            <!-- 期末可用资金行 -->
            <tr class="category-row">
              <td class="seq-col"></td>
              <td class="item-col">本息备付率</td>
              <td class="total-col">
                {{ formatNumber(projectData.addedFunds.本息备付率total) }}
              </td>
              <td
                class="year-col"
                v-for="year in projectData.calculationPeriod || 15"
                :key="`repayment-10closingFu9nds-${year}`"
              >
                {{ formatNumber(projectData.addedFunds.本息备付率[year - 1]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { calculateRepaymentPlan } from "@/utils/comp";

export default {
  name: "Repayment",
  props: {
    isViewMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      repaymentSource: "netProfit",

      projectData: null, // 本地存储项目数据
    };
  },
  computed: {},
  watch: {},
  created() {
    this.projectData = calculateRepaymentPlan() || {};

    // 确保必要字段存在
    if (!this.projectData.constructionPeriod) {
      this.projectData.constructionPeriod = 0;
    }
    if (!this.projectData.calculationPeriod) {
      this.projectData.calculationPeriod = 15;
    }

    // 初始化还款数据
    if (!this.projectData.repaymentData) {
      this.projectData.repaymentData = [];
    }
    this.repaymentSource = this.projectData.repaymentSource;
    if (!this.projectData.addedFunds) {
      this.projectData.addedFunds = {
        期初可用还款资金: [],
        本年新增还款资金: [],
        当期本金还款: [],
        当期利息还款: [],
        期末可用资金: [],
        期初可用还款资金total: 0,
        本年新增还款资金total: 0,
        当期本金还款total: 0,
        当期利息还款total: 0,
        期末可用资金total: 0,
      };
    }
  },
  mounted() {},
  methods: {
    // 格式化数字
    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "";
      if (num === 0) return "";
      return Number(num)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // 处理还款来源变化
    handleRepaymentSourceChange() {
      this.projectData = calculateRepaymentPlan(this.repaymentSource);
    },
  },
};
</script>

<style scoped>
/* 与原辅材料组件一致的样式 */
.input-area {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px 20px;
  background-color: white;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.input-row {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  flex-wrap: wrap;
}

.input-group {
  display: flex;
  align-content: center;
  gap: 6px;
}

.input-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
  padding-top: 8px;
  font-weight: 500;
}

.input-select {
  width: 150px;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  background-color: white;
  cursor: pointer;
}

.input-select:focus {
  border-color: #409eff;
  outline: none;
}

.table-wrapper {
  position: relative;
  width: 99.5%;
  height: calc(100vh - 280px);
  margin: auto;
  min-height: 400px;
  max-height: 600px;
  overflow: hidden;
  background-color: white;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.table-scroll-area {
  width: 100%;
  height: 100%;
  overflow: auto;
  position: relative;
}

.repayment-table {
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
  table-layout: fixed;
}

.repayment-table th,
.repayment-table td {
  border: 1px solid #e4e7ed;
  padding: 6px 6px;
  text-align: center;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.repayment-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #606266;
}

.repayment-table td {
  color: #606266;
}

.seq-col {
  width: 45px;
  min-width: 45px;
}

.item-col {
  width: 140px;
  min-width: 140px;
  text-align: left;
}

.total-col {
  width: 80px;
  min-width: 80px;
  font-weight: 600;
}

.year-col {
  width: 55px;
  min-width: 55px;
}

.period-type-col {
  padding: 4px 2px !important;
  font-size: 11px;
}

.construction-year {
  background-color: #e6f7ff;
}

.operation-year {
  background-color: #f6ffed;
}

.category-row {
  background-color: #f5f7fa;
}

.category-row td {
  font-weight: 600;
  color: #303133;
}

.detail-row td {
  background-color: white;
}

.detail-row td.item-col {
  text-align: left;
}
</style>
